import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import socket from "../views/pages/Sales1/socket.js";
import { setId } from "../Slices/employeeslice.js";
import { timerHistoryPayloadChange, timerPayloadUpdate } from "../Slices/user";
import { Starttask, updateLoginWorkHistoryThunk } from "../Slices/timerslice.jsx";
import { setLoginUserDetails } from "../Slices/loginUserSlice.jsx";
import { setLoader } from "../Slices/loaderSlice.jsx";
import { attendanceUpdateAsync } from "../Slices/Thunk/LoginThunk.js";
import { updateWorkHistoryThunk } from "../Slices/orderSlice.jsx";
import { hardLogoutReload } from "../databucket";
import { resetLayout } from "./layout/desktopLayoutSlice";
import { clearClipboard } from "./webtopUiSlice";

/**
 * useAppLogout — the app's real logout flow, extracted from the legacy Header so
 * the Webtop shell (which replaced the Header at cutover) can reuse it verbatim.
 *
 * Mirrors Header.HandleLogout/performLogout: punch-out prompt (Punch Out / Logout
 * / Both) when on the clock, work-history + login-history updates, socket
 * teardown, then clearLocalStorageSafe() + back to the login page ("/").
 * The legacy Header keeps its own copy (archived, no longer rendered).
 */
export const useAppLogout = () => {
  const dispatch = useDispatch();
  const loginUser = useSelector((s) => s?.loginUserInfo?.loginUserDetails);
  const UserStatus = useSelector((s) => s?.loginUserInfo?.punchinStatus);
  const { timerHistoryPayload } = useSelector((s) => s.user);
  const lastAttendenceRecord = useSelector((s) => s.Attendence?.attendence);
  const ID = lastAttendenceRecord?.id;
  const userid = loginUser?.id;
  const profileDate = loginUser;

  const performLogout = async () => {
    try {
      socket.off("newGraphNotification");
      socket.off("registerUser");
      socket.removeAllListeners();
      socket.disconnect();
      socket.close();
    } catch (e) { /* socket already down */ }

    const Logoutpayload = { department: "", task: "", time: "", requestId: "" };
    dispatch(setId(false));
    const payload = { logOut: true };
    await dispatch(updateLoginWorkHistoryThunk({ id: userid, endTime: new Date().toISOString() }));
    dispatch(timerHistoryPayloadChange({ data: payload }));
    dispatch(timerPayloadUpdate({ data: payload }));

    const logoutUpdatePayload = {
      userId: userid,
      endTime: Date.now().toString(),
      emailId: "",
      requestId: "",
      clientName: "Blueplanit",
      processType: "",
      taskType: "Loggingout",
      email: "",
      subject: "",
      body: "",
      startTime: "",
    };
    try {
      const result = await dispatch(updateWorkHistoryThunk(logoutUpdatePayload));
      if (result) {
        if (!timerHistoryPayload?.logOut) {
          dispatch(Starttask(Logoutpayload));
        } else {
          dispatch(timerPayloadUpdate());
        }
        // Drop the previous user's in-memory Webtop state first (defense in depth),
        // then hard-reload to the login screen. The reload is what actually
        // guarantees isolation on a shared machine — it wipes the entire Redux
        // store and Apollo cache, which a soft navigate("/") would leave intact.
        dispatch(resetLayout());
        dispatch(clearClipboard());
        hardLogoutReload(); // full clear + reload to "/" (login)
      } else {
        // eslint-disable-next-line no-alert
        alert("An Error Occurred while Updating Timer");
      }
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const logout = async () => {
    if (!UserStatus) {
      await performLogout();
      return;
    }
    const result = await Swal.fire({
      title: "Choose an Action",
      text: "Punch Out to clock out, Logout to sign out, or Both.",
      icon: "warning",
      showDenyButton: true,
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: "Punch Out",
      denyButtonText: "Logout",
      cancelButtonText: "Both",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: true,
      customClass: {
        closeButton: "swal-no-border",
        confirmButton: "swal2-confirm",
        denyButton: "swal2-cancel",
        cancelButton: "swal2-cancel-red",
      },
    });

    if (result.isConfirmed) {
      // Punch Out only — stay signed in.
      await dispatch(attendanceUpdateAsync({
        id: ID,
        empid: loginUser?.id,
        input: { punchOut: Date.now().toString(), status: false },
      }));
      dispatch(setLoader(false));
    } else if (result.isDenied) {
      // Logout only.
      await performLogout();
      dispatch(setLoader(false));
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      // Both — punch out then logout.
      await dispatch(attendanceUpdateAsync({
        id: ID,
        empid: loginUser?.id,
        input: { punchOut: Date.now().toString(), status: false },
      }));
      dispatch(setLoginUserDetails({ ...profileDate, punchInStatus: false }));
      await performLogout();
    }
  };

  return logout;
};

export default useAppLogout;
